import { useMemo } from 'react';
import { TourType } from '@/Components/Tour/Tour';
import { useLocalization } from '@/Hooks';
import { warehouseLocationSettingsActions } from '@/Pages/Settings/modules/WarehouseSettings/services/warehouseLocations/reducer';
import { useDispatch } from 'react-redux';
import { warehouseTypeSettingsActions } from '@/Pages/Settings/modules/WarehouseSettings/services/warehouseTypes/reducer';
import { warehouseTemplateSettingsActions } from '@/Pages/Settings/modules/WarehouseSettings/services/warehouseTemplates/reducer';

export function useWarehouseSettings() {
  const dispatch = useDispatch();

  const {
    tours: {
      WarehouseSettings: {
        PageTour,
        WarehouseTypeTour,
        WarehouseLocationTour,
        WarehouseTemplateTour,
        TemplateUnitTour,
      },
    },
  } = useLocalization();

  const pageTour = useMemo<TourType>(
    () => ({
      id: 'pageTour',
      title: PageTour.Title,
      steps: [
        {
          content: PageTour.Step1.Text,
          placement: 'auto',
          target: '#warehouseTitle',
          title: PageTour.Step1.Title,
          disableBeacon: true,
          link: PageTour.Step1.DetailsLink,
        },
        {
          content: PageTour.Step2.Text,
          placement: 'auto',
          target: '#warehouseTypes',
          title: PageTour.Step2.Title,
          disableBeacon: true,
          link: PageTour.Step2.DetailsLink,
        },
        {
          content: PageTour.Step3.Text,
          placement: 'auto',
          target: '#warehouseType',
          title: PageTour.Step3.Title,
          disableBeacon: true,
          link: PageTour.Step3.DetailsLink,
        },
        {
          content: PageTour.Step4.Text,
          placement: 'auto',
          target: '#addWarehouseType',
          title: PageTour.Step4.Title,
          disableBeacon: true,
          link: PageTour.Step4.DetailsLink,
        },
        {
          content: PageTour.Step5.Text,
          placement: 'auto',
          target: '#crudExtended',
          title: PageTour.Step5.Title,
          disableBeacon: true,
          link: PageTour.Step5.DetailsLink,
        },
        {
          content: PageTour.Step6.Text,
          placement: 'auto',
          target: '#warehouseLocations',
          title: PageTour.Step6.Title,
          disableBeacon: true,
          link: PageTour.Step6.DetailsLink,
        },
        {
          content: PageTour.Step7.Text,
          placement: 'auto',
          target: '#warehouseLocation',
          title: PageTour.Step7.Title,
          disableBeacon: true,
          link: PageTour.Step7.DetailsLink,
        },
        {
          content: PageTour.Step8.Text,
          placement: 'auto',
          target: '#addWarehouseLocation',
          title: PageTour.Step8.Title,
          disableBeacon: true,
          link: PageTour.Step8.DetailsLink,
        },
        {
          content: PageTour.Step9.Text,
          placement: 'auto',
          target: '#warehouseTemplates',
          title: PageTour.Step9.Title,
          disableBeacon: true,
          link: PageTour.Step9.DetailsLink,
        },
        {
          content: PageTour.Step10.Text,
          placement: 'auto',
          target: '#warehouseTemplate',
          title: PageTour.Step10.Title,
          disableBeacon: true,
          link: PageTour.Step10.DetailsLink,
        },
        {
          content: PageTour.Step11.Text,
          placement: 'auto',
          target: '#addWarehouseTemplate',
          title: PageTour.Step11.Title,
          disableBeacon: true,
          link: PageTour.Step11.DetailsLink,
        },
      ],
    }),
    [PageTour],
  );

  const warehouseTypeTour = useMemo<TourType>(
    () => ({
      id: 'warehouseTypeTour',
      title: WarehouseTypeTour.Title,
      before: () => {
        dispatch(warehouseTypeSettingsActions.setWarehouseTypeModal({ visible: true }));
      },
      steps: [
        {
          content: WarehouseTypeTour.Step1.Text,
          placement: 'auto',
          target: '#warehouseTypeTitle',
          title: WarehouseTypeTour.Step1.Title,
          disableBeacon: true,
          link: WarehouseTypeTour.Step1.DetailsLink,
        },
        {
          content: WarehouseTypeTour.Step2.Text,
          placement: 'auto',
          target: '#warehouseTypeName',
          title: WarehouseTypeTour.Step2.Title,
          disableBeacon: true,
          link: WarehouseTypeTour.Step2.DetailsLink,
        },
        {
          content: WarehouseTypeTour.Step3.Text,
          placement: 'auto',
          target: '#warehouseTypeColor',
          title: WarehouseTypeTour.Step3.Title,
          disableBeacon: true,
          link: WarehouseTypeTour.Step3.DetailsLink,
        },
        {
          content: WarehouseTypeTour.Step4.Text,
          placement: 'auto',
          target: '#warehouseTypeActive',
          title: WarehouseTypeTour.Step4.Title,
          disableBeacon: true,
          link: WarehouseTypeTour.Step4.DetailsLink,
        },
        {
          content: WarehouseTypeTour.Step5.Text,
          placement: 'auto',
          target: '#warehouseTypeFlags',
          title: WarehouseTypeTour.Step5.Title,
          disableBeacon: true,
          link: WarehouseTypeTour.Step5.DetailsLink,
        },
        {
          content: WarehouseTypeTour.Step6.Text,
          placement: 'auto',
          target: '#buttonsWrapper',
          title: WarehouseTypeTour.Step6.Title,
          disableBeacon: true,
          link: WarehouseTypeTour.Step6.DetailsLink,
        },
      ],
    }),
    [WarehouseTypeTour],
  );

  const warehouseLocationTour = useMemo<TourType>(
    () => ({
      id: 'warehouseLocationTour',
      title: WarehouseLocationTour.Title,
      before: () => {
        dispatch(warehouseLocationSettingsActions.setWarehouseLocationModal({ visible: true }));
      },
      steps: [
        {
          content: WarehouseLocationTour.Step1.Text,
          placement: 'auto',
          target: '#warehouseLocationTitle',
          title: WarehouseLocationTour.Step1.Title,
          disableBeacon: true,
          link: WarehouseLocationTour.Step1.DetailsLink,
        },
        {
          content: WarehouseLocationTour.Step2.Text,
          placement: 'auto',
          target: '#warehouseLocationActive',
          title: WarehouseLocationTour.Step2.Title,
          disableBeacon: true,
          link: WarehouseLocationTour.Step2.DetailsLink,
        },
        {
          content: WarehouseLocationTour.Step3.Text,
          placement: 'auto',
          target: '#locationInfo',
          title: WarehouseLocationTour.Step3.Title,
          disableBeacon: true,
          link: WarehouseLocationTour.Step3.DetailsLink,
        },
        {
          content: WarehouseLocationTour.Step4.Text,
          placement: 'auto',
          target: '#addressInfo',
          title: WarehouseLocationTour.Step4.Title,
          disableBeacon: true,
          link: WarehouseLocationTour.Step4.DetailsLink,
        },
        {
          content: WarehouseLocationTour.Step5.Text,
          placement: 'auto',
          target: '#buttonsWrapper',
          title: WarehouseLocationTour.Step5.Title,
          disableBeacon: true,
          link: WarehouseLocationTour.Step5.DetailsLink,
        },
      ],
    }),
    [WarehouseLocationTour],
  );

  const warehouseTemplateTour = useMemo<TourType>(
    () => ({
      id: 'warehouseTemplateTour',
      title: WarehouseTemplateTour.Title,
      before: () => {
        dispatch(warehouseTemplateSettingsActions.setWarehouseTemplateModal({ visible: true }));
      },
      steps: [
        {
          content: WarehouseTemplateTour.Step1.Text,
          placement: 'auto',
          target: '#warehouseTemplateTitle',
          title: WarehouseTemplateTour.Step1.Title,
          disableBeacon: true,
          link: WarehouseTemplateTour.Step1.DetailsLink,
        },
        {
          content: WarehouseTemplateTour.Step2.Text,
          placement: 'auto',
          target: '#warehouseTemplateName',
          title: WarehouseTemplateTour.Step2.Title,
          disableBeacon: true,
          link: WarehouseTemplateTour.Step2.DetailsLink,
        },
        {
          content: WarehouseTemplateTour.Step3.Text,
          placement: 'auto',
          target: '#templateItems',
          title: WarehouseTemplateTour.Step3.Title,
          disableBeacon: true,
          link: WarehouseTemplateTour.Step3.DetailsLink,
        },
        {
          content: WarehouseTemplateTour.Step4.Text,
          placement: 'auto',
          target: '#templateBlock',
          title: WarehouseTemplateTour.Step4.Title,
          disableBeacon: true,
          link: WarehouseTemplateTour.Step4.DetailsLink,
        },
        {
          content: WarehouseTemplateTour.Step5.Text,
          placement: 'auto',
          target: '#createTemplateItem',
          title: WarehouseTemplateTour.Step5.Title,
          disableBeacon: true,
          link: WarehouseTemplateTour.Step5.DetailsLink,
        },
        {
          content: WarehouseTemplateTour.Step6.Text,
          placement: 'auto',
          target: '#buttonsWrapper',
          title: WarehouseTemplateTour.Step6.Title,
          disableBeacon: true,
          link: WarehouseTemplateTour.Step6.DetailsLink,
        },
      ],
    }),
    [WarehouseTemplateTour],
  );

  const templateUnitTour = useMemo<TourType>(
    () => ({
      id: 'templateUnitTour',
      title: TemplateUnitTour.Title,
      before: () => {
        dispatch(warehouseTemplateSettingsActions.setWarehouseTemplateModal({ visible: true }));
        dispatch(warehouseTemplateSettingsActions.setNeedShowUnitModal({ visible: true }));
      },
      steps: [
        {
          content: TemplateUnitTour.Step1.Text,
          placement: 'auto',
          target: '#templateUnitTitle',
          title: TemplateUnitTour.Step1.Title,
          disableBeacon: true,
          link: TemplateUnitTour.Step1.DetailsLink,
        },
        {
          content: TemplateUnitTour.Step2.Text,
          placement: 'auto',
          target: '#templateUnitName',
          title: TemplateUnitTour.Step2.Title,
          disableBeacon: true,
          link: TemplateUnitTour.Step2.DetailsLink,
        },
      ],
    }),
    [TemplateUnitTour],
  );

  return [pageTour, warehouseTypeTour, warehouseLocationTour, warehouseTemplateTour, templateUnitTour];
}
